import React, { RefObject, useMemo, useState } from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { Filter } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { ellipsis } from '@fabric-ui/utils';
import { Box, IconButton, TableCell } from '@mui/material';
import { flexRender, Header } from '@tanstack/react-table';
import { AvalorTableIds, isNumberType } from '../Table';
import { useAvTableV2Context } from './AvTableV2Provider';
import { ResizeHandle } from './ResizeHandle';
import SortableHeaderCell from './SortableHeaderCell';
import { DataRecord, FormatterType } from './table.types';
import { getSizeKey } from './table.utils';

interface AvTableV2HeadCellProps {
  header: Header<DataRecord, unknown>;
  ref: RefObject<HTMLDivElement | null>;
  FieldFilter: any;
}

const filterIcon = <Filter />;

const getDefaultFilterFunc = (colId: string) => (row: DataRecord) => ({
  value: !!row[colId],
  title: row[colId] ? 'True' : 'False',
});

export const AvTableV2HeadCell = ({ header, ref: tableContainerRef, FieldFilter }: AvTableV2HeadCellProps) => {
  const { table } = useAvTableV2Context();
  const { filter: filterConfig, type: columnType, isColumnHighlighted, style } = header.column.columnDef.meta || {};
  const { asyncGetOptions, getOptions, options } = filterConfig || {};

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const isLastHeader = header.column.getIsLastColumn();

  const isFiltered = header.column.getIsFiltered();
  const enabledFilter = header.column.getCanFilter();

  const getFilterOptionFunc =
    filterConfig?.getFilterOptionFunc ?? (columnType === FormatterType.boolean ? getDefaultFilterFunc(header.column.id) : undefined);

  const filterOptions = useMemo(() => {
    if (!enabledFilter || asyncGetOptions) return undefined;

    if (options) return options;

    if (columnType === FormatterType.boolean && !getFilterOptionFunc) {
      return [
        { value: true, title: 'True' },
        { value: false, title: 'False' },
      ];
    }

    const facetedValues = header.column.getFacetedUniqueValues();
    if (!facetedValues) {
      return [];
    }

    const optionMap = Array.from(facetedValues.keys()).reduce((acc, rawValue) => {
      const option = getFilterOptionFunc
        ? getFilterOptionFunc({ [header.column.id]: rawValue })
        : { value: rawValue, title: String(rawValue) };

      if (!acc.has(option.value)) {
        acc.set(option.value, option);
      }
      return acc;
    }, new Map<any, { value: any; title: string }>());

    return Array.from(optionMap.values());
  }, [asyncGetOptions, options, header.column, getFilterOptionFunc, columnType, enabledFilter]);

  const filterIconButton = enabledFilter && columnType !== FormatterType.date && (
    <FieldFilter
      onOpen={() => setIsFilterOpen(true)}
      onClose={() => setIsFilterOpen(false)}
      fieldType={columnType as FormatterType}
      isMultipleCondition={false}
      isEnabled={false}
      trigger={
        <IconButton
          className="filter"
          sx={{
            opacity: isFiltered || isFilterOpen ? 1 : 0,
          }}
          {...(isFiltered ? { color: 'primary' } : {})}>
          {filterIcon}
        </IconButton>
      }
      field={header.column.id}
      filters={{ [header.column.id]: header.column.getFilterValue() }}
      onChange={(_, value) => header.column.setFilterValue(value)}
      options={filterOptions}
      asyncGetOptions={asyncGetOptions}
      getOptions={getOptions}
    />
  );

  const isSorted = header.column.getIsSorted();
  const isSelectAllHeader = header.id === AvalorTableIds.SelectAllId;
  const isResizingColumn = !!table.getState().columnSizingInfo.isResizingColumn;
  const dynamicWidth = table.options.enableColumnResizing ? `calc(var(${getSizeKey(header.id, 'header')}) * 1px)` : undefined;

  const resetSize = () => {
    const initialSize = table.options.meta?.initialSizing?.current?.[header.column.id];
    if (initialSize !== undefined) {
      table.setColumnSizing(prev => ({ ...prev, [header.column.id]: initialSize }));
    } else {
      header.column.resetSize();
    }
  };

  return (
    <TableCell
      key={header.id}
      data-column-id={header.column.id}
      align={isNumberType(columnType) ? 'right' : 'left'}
      padding={isSelectAllHeader ? 'checkbox' : undefined}
      className={isColumnHighlighted ? 'active-column' : ''}
      sx={{
        ...style,
        width:
          header.id === AvalorTableIds.FreeSpaceId
            ? 'calc(max(0px, var(--container-width, 0px) - var(--total-defined-column-size, 0px)))'
            : dynamicWidth,
        cursor: 'default',
        ':hover .sort-icon': {
          opacity: header.column.getIsSorted() ? 1 : 0.6,
        },
        ...(!isLastHeader && {
          ':hover .drag-handle, :has(+ :hover) .drag-handle': {
            opacity: !isResizingColumn || header.column.getIsResizing() ? 1 : 0,
          },
        }),
        '.sort-icon, .sort-icon-box': { display: isSorted || !header.column.getCanResize() ? 'flex' : 'none' },
        ':hover': {
          '.MuiIconButton-root': { opacity: 1 },
          '.sort-icon, .sort-icon-box': { opacity: isSorted ? 1 : isResizingColumn ? 0 : 0.6, ...flex.row },
          '.filter': { ...flex.row, opacity: isResizingColumn ? 0 : 1 },
        },
      }}>
      <Box
        sx={{
          ...flex.itemsCenter,
          height: '100%',
          position: 'relative',
          ...(isNumberType(columnType) ? { pr: 1 } : {}),
        }}>
        <Box
          sx={{
            ...flex.itemsCenter,
            justifyContent: isNumberType(columnType) ? 'flex-end' : 'flex-start',
            width: '100%',
          }}>
          {header.isPlaceholder ? null : isSelectAllHeader ? (
            flexRender(header.column.columnDef.header, header.getContext())
          ) : header.column.getCanSort() ? (
            <SortableHeaderCell
              isSorted={isSorted}
              initialSortDirection={header.column.getFirstSortDir()}
              columnType={columnType}
              toggleSortingHandler={header.column.getToggleSortingHandler()}
              filterIconButton={filterIconButton}>
              {flexRender(header.column.columnDef.header, header.getContext())}
            </SortableHeaderCell>
          ) : (
            <>
              <Box sx={{ ...ellipsis, mr: 0.5 }}>
                <AvTooltip>{flexRender(header.column.columnDef.header, header.getContext())}</AvTooltip>
              </Box>
              {filterIconButton}
            </>
          )}
        </Box>
      </Box>
      {header.column.getCanResize() && (
        <ResizeHandle
          resizeHandler={header.getResizeHandler()}
          resetSize={resetSize}
          isResizing={header.column.getIsResizing()}
          dragLineHeight={tableContainerRef?.current?.clientHeight || 0}
        />
      )}
    </TableCell>
  );
};
